import React from 'react';
import { Link, NavLink, withRouter } from 'react-router-dom';
import { Route, RouteComponentProps, matchPath } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';
import { setRef } from '@wix/da-ds/pkg/utils/refHelpers';
import withFeatureFlags, {
  WithFeatureFlagsProps,
} from '@wix/da-react-context/pkg/flags/withFeatureFlags';
import UnderlinedTabs from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs';
import SophisticatedFtueTooltip from '@wix/da-shared-react/pkg/Popover/Preset/SophisticatedFtueTooltip';
import { FirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/constants';
import { Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import classnames from 'classnames';
import { Route as RouteDefinition } from '../../../../types/route';

import s from './SubNavBarMobile.scss';

export interface SubNavBarProps {
  routes: RouteDefinition[];
  isSticky: boolean;
  biClickProfileNavBar: (linkName: string) => void;
}

class SubNavBarMobileInner extends React.PureComponent<
  SubNavBarProps &
    WithTranslation &
    RouteComponentProps<any> &
    WithFeatureFlagsProps
> {
  navRef: React.RefObject<HTMLDivElement> = React.createRef();
  navListRef: React.RefObject<HTMLDivElement> = React.createRef();
  activeRouteRef: React.RefObject<any> = React.createRef();

  componentDidMount() {
    this.scrollToActive();
  }

  render() {
    const { isSticky, routes, biClickProfileNavBar, t, flags } = this.props;
    const isDesignUpdateFlagEnabled = flags.includes('subnav_design_update');
    return (
      <Nav
        className={classnames(
          s['navbar'],
          isSticky && s['sticky'],
          isDesignUpdateFlagEnabled && s['design-update']
        )}
        ref={this.navRef}
      >
        <VisuallyHidden>
          <Heading>{t('navigation.subnavigation.heading')}</Heading>
        </VisuallyHidden>

        <div className={s['nav-list']} ref={this.navListRef}>
          <Route
            key="nav-user"
            path="/:username"
            render={({ match, location }) =>
              isDesignUpdateFlagEnabled ? (
                <UnderlinedTabs
                  className={s['nav-list']}
                  activeItemRef={node => setRef(this.activeRouteRef, node)}
                  activeIndex={routes.findIndex(
                    route =>
                      // react-router is having trouble letting us know which route is the active one outside
                      !!matchPath(location.pathname, {
                        path: route.path,
                        exact: route.exact,
                      })
                  )}
                  items={routes.map(
                    (route, index) =>
                      ({
                        label: (
                          <Link
                            key={route.key}
                            to={(route.path || '')
                              .replace(/:username/, match.params.username)
                              .replace(/\/:[a-z0-9_()\\+]+\?/gi, '')}
                            exact={route.exact}
                            onClick={() => biClickProfileNavBar(route.key)}
                          >
                            {route.key === 'subscriptions' ? (
                              <SophisticatedFtueTooltip
                                flag={FirstTimeFlag.IsSubscriptionsTabNotViewed}
                                size="medium"
                                placement="bottom"
                                hasCloseButton
                                usePortal
                                contentClassName={s['nav-item-tooltip']}
                                aria-label={t(
                                  'navigation.route.subscriptions.ftue.a11y.label'
                                )}
                                postfix={
                                  <span
                                    className={s['nav-item-tooltip-content']}
                                  >
                                    {t('navigation.route.subscriptions.ftue')}
                                  </span>
                                }
                              >
                                {t(`navigation.route.var.${route.key}`)}
                              </SophisticatedFtueTooltip>
                            ) : (
                              t(`navigation.route.var.${route.key}`)
                            )}
                          </Link>
                        ),
                        labelExtras: route.isBeta && (
                          <span className={s['beta']}>beta</span>
                        ),
                        index,
                      } as any)
                  )}
                />
              ) : (
                routes.map(route => (
                  <NavLink
                    key={route.key}
                    to={(route.path || '')
                      .replace(/:username/, match.params.username)
                      .replace(/\/:[a-z0-9_()\\+]+\?/gi, '')}
                    exact={route.exact}
                    className={s['nav-item']}
                    activeClassName={s['active']}
                    onClick={() => biClickProfileNavBar(route.key)}
                  >
                    {route.key === 'subscriptions' ? (
                      <SophisticatedFtueTooltip
                        flag={FirstTimeFlag.IsSubscriptionsTabNotViewed}
                        size="medium"
                        placement="bottom"
                        hasCloseButton
                        usePortal
                        contentClassName={s['nav-item-tooltip']}
                        aria-label={t(
                          'navigation.route.subscriptions.ftue.a11y.label'
                        )}
                        postfix={
                          <span className={s['nav-item-tooltip-content']}>
                            {t('navigation.route.subscriptions.ftue')}
                          </span>
                        }
                      >
                        {t(`navigation.route.var.${route.key}`)}
                      </SophisticatedFtueTooltip>
                    ) : (
                      t(`navigation.route.var.${route.key}`)
                    )}
                    {route.isBeta && <span className={s['beta']}>beta</span>}
                  </NavLink>
                ))
              )
            }
          />
        </div>
      </Nav>
    );
  }

  scrollToActive = () => {
    const { flags } = this.props;

    if (!this.navRef.current || !this.navListRef.current) {
      return;
    }
    let activeRouteElement;

    if (flags.includes('subnav_design_update')) {
      activeRouteElement = this.activeRouteRef.current;
    } else {
      activeRouteElement = Array.from(this.navListRef.current.children).find(
        routeItem => (routeItem as HTMLElement).classList.contains(s['active'])
      ) as HTMLElement;
    }

    if (!activeRouteElement) {
      return;
    }

    this.navRef.current.scrollLeft =
      activeRouteElement.offsetLeft -
      this.navRef.current.getBoundingClientRect().width / 2;
  };
}

export default withRouter(
  withFeatureFlags(withTranslation()(SubNavBarMobileInner))
);
